import sanitizeHtml from 'sanitize-html';

import type { FlatGraph } from '@/sections/insights/queryBuilder/types/FlatGraph';
import type { QueryFilter } from '@/types';
import type { ValidationResult } from '@/utils/validation.util';
import { isValidNodeLabel, isValidRelationship } from '@/utils/validation.util';

/**
 * Maximum number of nodes allowed in a query (prevents DoS)
 */
const MAX_NODES = 1000;

/**
 * Maximum number of edges allowed in a query (prevents DoS)
 */
const MAX_EDGES = 5000;

/**
 * Filter validation rules:
 * - Rule 6: Non-IN operators must have at most one value
 * - Rule 7: Non-NULL operators must have at least one value
 * - Rule 8: Logical operators (OR/AND) must not have a field
 *
 * Applied recursively to handle nested filters in OR/AND groups.
 */

/**
 * Validates a query graph structure before execution or saving.
 *
 * Validation rules (fail-fast order):
 * 0. Size limits (DoS prevention)
 * 1. Empty query check
 * 2. Connectivity check (all nodes reachable)
 * 3. Circular path detection
 * 4. Node label validation
 * 5. Relationship label validation
 * 6. Filter validation (operator-specific rules)
 *
 * @param flatGraph - The query graph to validate
 * @returns ValidationResult with first error encountered
 */
export function validateQuery(flatGraph: FlatGraph): ValidationResult {
  // Rule 0: Size limits (DoS prevention)
  if (flatGraph.nodes.size > MAX_NODES) {
    return {
      isValid: false,
      errors: [`Query too complex: maximum ${MAX_NODES} nodes allowed`],
    };
  }

  if (flatGraph.edges.size > MAX_EDGES) {
    return {
      isValid: false,
      errors: [`Query too complex: maximum ${MAX_EDGES} edges allowed`],
    };
  }

  // Rule 1: Empty query check
  if (flatGraph.nodes.size === 0) {
    return {
      isValid: false,
      errors: ['Query must have at least one node'],
    };
  }

  // Rule 2: Connectivity check (BFS)
  const visited = new Set<string>();
  const queue: string[] = [flatGraph.rootId];

  while (queue.length > 0) {
    const nodeId = queue.shift()!;
    if (visited.has(nodeId)) continue;

    visited.add(nodeId);

    // Find all connected edges (bidirectional - treat as undirected graph)
    for (const edge of flatGraph.edges.values()) {
      // Forward direction: source → target
      if (edge.sourceId === nodeId && !visited.has(edge.targetId)) {
        queue.push(edge.targetId);
      }
      // Backward direction: target → source (for reachability)
      if (edge.targetId === nodeId && !visited.has(edge.sourceId)) {
        queue.push(edge.sourceId);
      }
    }
  }

  // Check if all nodes were visited
  if (visited.size !== flatGraph.nodes.size) {
    return {
      isValid: false,
      errors: ['Query has disconnected nodes. All nodes must be connected.'],
    };
  }

  // Rule 3: Circular path detection (DFS)
  const visiting = new Set<string>(); // Current DFS path
  const visitedDFS = new Set<string>(); // Fully explored nodes

  /**
   * DFS helper to detect cycles
   * @returns true if cycle detected, false otherwise
   */
  function hasCycle(nodeId: string): boolean {
    if (visiting.has(nodeId)) {
      // Node in current path → cycle detected
      return true;
    }

    if (visitedDFS.has(nodeId)) {
      // Already fully explored → no cycle from this node
      return false;
    }

    visiting.add(nodeId);

    // Check all outgoing edges
    for (const edge of flatGraph.edges.values()) {
      if (edge.sourceId === nodeId) {
        if (hasCycle(edge.targetId)) {
          return true;
        }
      }
    }

    // Backtrack: remove from current path, mark as fully explored
    visiting.delete(nodeId);
    visitedDFS.add(nodeId);
    return false;
  }

  // Check for cycles starting from each unvisited node
  for (const nodeId of flatGraph.nodes.keys()) {
    if (!visitedDFS.has(nodeId)) {
      if (hasCycle(nodeId)) {
        return {
          isValid: false,
          errors: [
            'Query contains a circular path. Circular paths are not allowed.',
          ],
        };
      }
    }
  }

  // Rule 4: Node label validation
  for (const [, node] of flatGraph.nodes) {
    if (node.data.labels) {
      for (const label of node.data.labels) {
        if (!isValidNodeLabel(label)) {
          // Sanitize label to prevent XSS in error messages
          const sanitizedLabel = sanitizeHtml(label, {
            allowedTags: [],
            allowedAttributes: {},
          });
          return {
            isValid: false,
            errors: [`Invalid node label: '${sanitizedLabel}'`],
          };
        }
      }
    }
  }

  // Rule 5: Relationship label validation
  for (const [, edge] of flatGraph.edges) {
    // Handle both single label and array of labels
    const labels = Array.isArray(edge.data.label)
      ? edge.data.label
      : [edge.data.label];

    if (labels.length === 0) {
      return {
        isValid: false,
        errors: [`One or more relationships has zero labels`],
      };
    }

    for (const label of labels) {
      if (!isValidRelationship(label)) {
        // Sanitize label to prevent XSS in error messages
        const sanitizedLabel = sanitizeHtml(label, {
          allowedTags: [],
          allowedAttributes: {},
        });
        return {
          isValid: false,
          errors: [`Invalid relationship label: '${sanitizedLabel}'`],
        };
      }
    }
  }

  // Rule 6: Filter validation (includes Rules 6, 7, 8)
  for (const [, node] of flatGraph.nodes) {
    if (node.data.filters) {
      const result = validateFilters(node.data.filters);
      if (!result.isValid) {
        return result;
      }
    }
  }

  return { isValid: true, errors: [] };
}

/**
 * Type guard to check if a value is an array of QueryFilter objects
 * Used to distinguish between value arrays like [1, 2, 3] and nested filters
 */
function isFilterArray(value: unknown): value is QueryFilter[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    typeof value[0] === 'object' &&
    value[0] !== null &&
    'operator' in value[0]
  );
}

/**
 * Validates a single filter with operator-specific rules
 * Recursively validates nested filters for OR/AND operators
 *
 * @param filter - The filter to validate
 * @returns ValidationResult with first error encountered
 */
function validateFilter(filter: QueryFilter): ValidationResult {
  // Rule 8: Logical operators must not have a field
  if (filter.operator === 'OR' || filter.operator === 'AND') {
    if (filter.field && filter.field.trim() !== '') {
      const sanitizedOperator = sanitizeHtml(filter.operator, {
        allowedTags: [],
        allowedAttributes: {},
      });
      return {
        isValid: false,
        errors: [
          `Logical operator '${sanitizedOperator}' must not have a field`,
        ],
      };
    }
  }

  // Rule 6: Non-IN operators must have at most one value
  const multipleValuesAllowed = ['IN', 'AND', 'OR'].includes(filter.operator);

  if (
    !multipleValuesAllowed &&
    Array.isArray(filter.value) &&
    filter.value.length > 1
  ) {
    return {
      isValid: false,
      errors: [
        `Filter with operator '${filter.operator}' must have at most one value`,
      ],
    };
  }

  // Rule 7: Non-NULL operators must have at least one value
  if (filter.operator !== 'IS NULL' && filter.operator !== 'IS NOT NULL') {
    // Check for empty or undefined value
    if (filter.value === undefined || filter.value === null) {
      const sanitizedOperator = sanitizeHtml(filter.operator, {
        allowedTags: [],
        allowedAttributes: {},
      });
      return {
        isValid: false,
        errors: [
          `Filter with operator '${sanitizedOperator}' must have at least one value`,
        ],
      };
    }

    // Check for empty arrays
    if (Array.isArray(filter.value)) {
      if (filter.value.length === 0) {
        const sanitizedOperator = sanitizeHtml(filter.operator, {
          allowedTags: [],
          allowedAttributes: {},
        });
        return {
          isValid: false,
          errors: [
            `Filter with operator '${sanitizedOperator}' must have at least one value`,
          ],
        };
      }

      // Check for nested empty arrays [[]]
      if (
        Array.isArray(filter.value[0]) &&
        filter.value.every(arr => Array.isArray(arr) && arr.length === 0)
      ) {
        const sanitizedOperator = sanitizeHtml(filter.operator, {
          allowedTags: [],
          allowedAttributes: {},
        });
        return {
          isValid: false,
          errors: [
            `Filter with operator '${sanitizedOperator}' must have at least one value`,
          ],
        };
      }
    }
  }

  // Recurse into nested filters (for OR/AND operators)
  if (isFilterArray(filter.value)) {
    for (const nestedFilter of filter.value) {
      const result = validateFilter(nestedFilter);
      if (!result.isValid) {
        return result; // Fail-fast: return first error
      }
    }
  }

  return { isValid: true, errors: [] };
}

/**
 * Validates an array of filters
 * Calls validateFilter for each filter, failing fast on first error
 *
 * @param filters - Array of filters to validate
 * @returns ValidationResult with first error encountered
 */
function validateFilters(filters: QueryFilter[]): ValidationResult {
  for (const filter of filters) {
    const result = validateFilter(filter);
    if (!result.isValid) {
      return result; // Fail-fast
    }
  }

  return { isValid: true, errors: [] };
}
